import React, { useState, useEffect } from 'react';
import { PUBLIC_FN_TOKEN } from '@/lib/publicFnToken';
import { useNavigate, Link } from 'react-router-dom';
import DateRangePicker from '@/components/search/DateRangePicker';
import TimeSelect from '@/components/search/TimeSelect';
import SeoLangSwitcher from '@/components/seo/SeoLangSwitcher';
import CityAutocomplete from '@/components/landing/CityAutocomplete';
import { MapPin, MessageCircle, Phone, Star, Shield, Clock, Truck, CheckCircle, Search, Lock, Calendar } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { getStartStrings } from '@/components/landing/orthocareStartI18n';
import { isGestosAgeisDomain } from '@/lib/siteDomain';

const DEFAULT_HERO_IMG =
  'https://media.base44.com/images/public/691220492173ae6cbe68ab07/05c0ac79d_Screenshot_20260425_214044_Google.jpg';
// Distinct hero image for the gestosageis.pt domain (orthocare.pt keeps DEFAULT_HERO_IMG)
const GESTOS_HERO_IMG =
  'https://media.base44.com/images/public/691220492173ae6cbe68ab07/26b037cd1_generated_image.png';
// Domain-specific hero backgrounds for the orthocare family:
// .pt domains (orthocare.pt / orthocarerent.pt) → senior with rollator walker on a Portuguese street
// .com domain (orthocarerent.com) → senior on a mobility scooter in a park
const ORTHOCARE_PT_HERO =
  'https://media.base44.com/images/public/691220492173ae6cbe68ab07/bdbc15040_generated_image.png';
const ORTHOCARE_COM_HERO =
  'https://media.base44.com/images/public/691220492173ae6cbe68ab07/1274206ba_generated_image.png';
const ORTHOCARE_LOGO_URL =
  'https://media.base44.com/images/public/691220492173ae6cbe68ab07/eec4f6188_file_000000000f3871f4acdc11aca9b1a76e.png';
const GESTOS_LOGO_URL =
  'https://media.base44.com/images/public/691220492173ae6cbe68ab07/93a4b2fb8_1777227440051110206008173168802.png';

// Persistent browser identifier (UUID) stored in localStorage. Used to
// correlate simulations across brands (orthocare ↔ gestosageis) so a customer
// who simulates on one brand and then visits the other within 2 hours gets
// the form pre-filled with the previous search values.
const STORAGE_KEY = 'ga_browser_id';
function getBrowserId() {
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

// Normalize text for loose, accent/case-insensitive matching (e.g. "lisboa"
// matches "Lisboa", "Evora" matches "Évora"). Users often type without accents.
const normalizeText = (v) =>
  (v || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

// Reusable hero + booking widget + contact nav + stats bar, matching the /start
// landing page. Designed to sit at the top of SEO landing pages so visitors can
// run a rental simulation directly. Hero text is overridable per page (for SEO).
export default function OrthocareHeroSearch({ lang, hero, heroImage, forceGestos }) {
  const navigate = useNavigate();
  const S = getStartStrings(lang);
  const isGestos = forceGestos || isGestosAgeisDomain();
  const LOGO_URL = isGestos ? GESTOS_LOGO_URL : ORTHOCARE_LOGO_URL;
  const LOGO_ALT = isGestos ? 'Gestos Ágeis' : 'Orthocare Rent';
  const BRAND_SOURCE = isGestos ? 'gestosageis' : 'orthocare';
  const HOME_PATH = isGestos ? '/gestosageis' : '/start';
  // Domain-aware color theme. Gestos Ágeis uses its own green; orthocare.pt keeps
  // the existing navy palette untouched.
  const theme = isGestos
    ? { brand: '#0e6b4f', brandDark: '#0a4a37', brandDarker: '#063027', accent: '#c5a059', overlayRgb: '14,107,79' }
    : { brand: '#0a3452', brandDark: '#061f35', brandDarker: '#04121f', accent: '#cebe75', overlayRgb: '10,52,82' };

  const now = new Date();
  const isAfter22 = now.getHours() >= 22;
  const minStartDate = isAfter22
    ? new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];
  const defaultStartDate = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  // Default to a 1-week rental — the most common duration — so the first
  // price shown reflects a realistic (higher) ticket instead of a 1-day min.
  const defaultEndDate = new Date(new Date(defaultStartDate).getTime() + 7 * 86400000).toISOString().split('T')[0];

  const [deliveryHint, setDeliveryHint] = useState('');
  const [differentReturnCity, setDifferentReturnCity] = useState(false);
  const [searchData, setSearchData] = useState({
    city: '',
    returnCity: '',
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    pickupTime: '10:00',
    returnTime: '10:00',
  });

  // Single cached backend call instead of 2 direct entity reads per visitor.
  // getSearchPageData caches server-side for 2 min, so 1000 visitors = 1 DB
  // read instead of 2000. This is critical to stay under the read-volume limit.
  const { data: pageData } = useQuery({
    queryKey: ['search-page-data'],
    queryFn: async () => {
      const res = await base44.functions.invoke('getSearchPageData', {});
      return res.data;
    },
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });
  const settings = pageData?.baseFeeSettings || [];
  // Use ALL counties (concelhos + freguesias) for the city picker — the list
  // must match exactly what's configured in "Concelhos e Entregas" settings.
  const backendCounties = (pageData?.counties && pageData.counties.length > 0) ? pageData.counties : (pageData?.concelhos || []);
  // Fallback removed deliberately: it ran a 3000-row read for every visitor
  // whenever the shared payload was momentarily empty, which kept the app over
  // its entity read-volume limit. getSearchPageData now always answers, serving
  // its last good snapshot.
  const counties = backendCounties;

  // Detecta a localidade do utilizador e pré-preenche a cidade se for portuguesa.
  useEffect(() => {
    if (!counties.length || searchData.city) return;
    let cancelled = false;
    const matchCounty = (raw) => {
      if (!raw) return null;
      const key = normalizeText(raw);
      let m = counties.find((c) => normalizeText(c.name) === key);
      if (m) return m.name;
      m = counties.find((c) => normalizeText(c.name).includes(key) || key.includes(normalizeText(c.name)));
      return m ? m.name : null;
    };
    const applyFromAddress = (addr) => {
      if (cancelled) return;
      if (!addr || (addr.country_code && addr.country_code.toLowerCase() !== 'pt')) return;
      const raw = addr.county || addr.city || addr.town || addr.village || addr.municipality;
      const matched = matchCounty(raw);
      if (matched) setSearchData((s) => (s.city ? s : { ...s, city: matched }));
    };
    const reverseGeocode = async (lat, lon) => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=pt`);
        const d = await r.json();
        if (d && d.address) applyFromAddress(d.address);
      } catch {}
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => reverseGeocode(pos.coords.latitude, pos.coords.longitude),
        () => {},
        { timeout: 8000, maximumAge: 600000 }
      );
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counties.length]);

  // Cross-brand pre-fill: if this visitor recently simulated on the other
  // brand (same browser_id or IP, within 2 hours), pre-fill the form with
  // those values so they can compare/continue seamlessly.
  // Cached in sessionStorage (10-min TTL) to avoid a backend call on every
  // page mount — the result won't change within minutes for the same browser.
  useEffect(() => {
    let cancelled = false;
    const browserId = getBrowserId();
    if (!browserId) return;

    // Check sessionStorage cache first
    const CACHE_KEY = 'ga_cross_brand_prefill';
    const CACHE_TTL = 10 * 60 * 1000; // 10 minutes
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.ts < CACHE_TTL && parsed.data) {
          const m = parsed.data;
          if (m.found) {
            setSearchData((s) => ({
              ...s,
              city: m.city || s.city,
              returnCity: m.return_city || '',
              startDate: m.start_date || s.startDate,
              endDate: m.end_date || s.endDate,
              pickupTime: m.pickup_time || s.pickupTime,
              returnTime: m.return_time || s.returnTime,
            }));
            if (m.return_city) setDifferentReturnCity(true);
          }
          return; // Use cached result — no backend call
        }
      }
    } catch {}

    base44.functions.invoke('findRecentCrossBrandSearch', {
      browserId,
      currentSource: BRAND_SOURCE
    }).then((res) => {
      if (cancelled || !res?.data) return;
      // Cache the result
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: res.data })); } catch {}
      if (!res.data.found) return;
      const m = res.data;
      setSearchData((s) => ({
        ...s,
        city: m.city || s.city,
        returnCity: m.return_city || '',
        startDate: m.start_date || s.startDate,
        endDate: m.end_date || s.endDate,
        pickupTime: m.pickup_time || s.pickupTime,
        returnTime: m.return_time || s.returnTime,
      }));
      if (m.return_city) setDifferentReturnCity(true);
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const startDateObj = new Date(searchData.startDate);
    const endDateObj = new Date(searchData.endDate);
    const isToday = startDateObj.toDateString() === now.toDateString();
    let newState = { ...searchData };
    let changed = false;
    if (isToday) {
      const minTime = new Date(now.getTime() + 60 * 60 * 1000);
      let nextH = minTime.getHours();
      let nextM = minTime.getMinutes() < 30 ? 30 : 0;
      if (minTime.getMinutes() >= 30) nextH = (nextH + 1) % 24;
      const minStr = `${String(nextH).padStart(2, '0')}:${String(nextM).padStart(2, '0')}`;
      if (searchData.pickupTime < minStr) { newState.pickupTime = minStr; changed = true; }
    }
    if (endDateObj < startDateObj) { newState.endDate = searchData.startDate; changed = true; }
    const isSameDay = startDateObj.toDateString() === endDateObj.toDateString();
    if (isSameDay && newState.returnTime < newState.pickupTime) {
      newState.returnTime = newState.pickupTime;
      changed = true;
    }
    if (changed) setSearchData(newState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData.startDate, searchData.endDate, searchData.pickupTime]);

  const minPickupStr = (() => {
    const startDateObj = new Date(searchData.startDate);
    if (startDateObj.toDateString() !== now.toDateString()) return null;
    const minTime = new Date(Date.now() + 60 * 60 * 1000);
    let nextH = minTime.getHours();
    let nextM = minTime.getMinutes() < 30 ? 30 : 0;
    if (minTime.getMinutes() >= 30) nextH = (nextH + 1) % 24;
    return `${String(nextH).padStart(2, '0')}:${String(nextM).padStart(2, '0')}`;
  })();

  const isSameDayReturn = new Date(searchData.startDate).toDateString() === new Date(searchData.endDate).toDateString();
  const minReturnStr = isSameDayReturn ? searchData.pickupTime : null;

  const handleSearch = async () => {
    // Normalize typed locations to the exact county names stored in the DB so
    // the search only ever uses real, available locations. Matching is accent
    // and case insensitive (people often type "lisboa" or "Evora").
    const norm = (v) => {
      const val = (v || '').trim();
      if (!val) return val;
      const key = normalizeText(val);
      const m = counties.find((c) => normalizeText(c.name) === key);
      return m ? m.name : val;
    };
    const city = norm(searchData.city);
    const returnCity = norm(searchData.returnCity);
    const params = new URLSearchParams({
      city, startDate: searchData.startDate, endDate: searchData.endDate,
      pickupTime: searchData.pickupTime, returnTime: searchData.returnTime,
    });
    if (differentReturnCity && returnCity) params.set('returnCity', returnCity);
    if (deliveryHint) params.set('deliveryHint', deliveryHint);
    params.set('source', BRAND_SOURCE);
    sessionStorage.setItem('searchSource', BRAND_SOURCE);

    const resultsUrl = `${window.location.origin}/SearchResults?${params.toString()}`;
    // Always invoke — the function logs the search to SearchLog; SMS sending
    // is controlled by SMS_ENABLED inside the function itself.
    base44.functions.invoke('sendSearchSms', {
      fnToken: PUBLIC_FN_TOKEN,
      city, startDate: searchData.startDate, endDate: searchData.endDate,
      pickupTime: searchData.pickupTime, returnTime: searchData.returnTime,
      returnCity, source: BRAND_SOURCE, language: lang, resultsUrl,
      browserId: getBrowserId(),
    }).catch((err) => console.warn('sendSearchSms falhou:', err));
    navigate(`/SearchResults?${params.toString()}`);
  };

  const badge = hero?.badge ?? S.badge;
  const h1 = hero?.h1 ?? `${S.h1a} ${S.h1b} ${S.h1c}`;
  const heroP = hero?.heroP ?? S.heroP;
  const orthocareHeroImg = typeof window !== 'undefined' && window.location.hostname.endsWith('orthocarerent.com')
    ? ORTHOCARE_COM_HERO
    : ORTHOCARE_PT_HERO;
  const heroImg = heroImage ?? (isGestos ? GESTOS_HERO_IMG : orthocareHeroImg);
  const heroAlt = hero?.heroImgAlt ?? S.heroImgAlt;

  return (
    <>
      <style>{`
        :root { --brand: ${theme.brand}; --brand-dark: ${theme.brandDark}; --brand-darker: ${theme.brandDarker}; --accent: ${theme.accent}; --brand-overlay: ${theme.overlayRgb}; }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
        .hero-fade-1 { animation: fadeInUp 0.7s ease both; }
        .hero-fade-2 { animation: fadeInUp 0.7s ease 0.15s both; }
        .hero-fade-3 { animation: fadeInUp 0.7s ease 0.3s both; }
        .hero-fade-4 { animation: fadeInUp 0.7s ease 0.45s both; }
        .m4r-field { border: 1.5px solid var(--brand); border-radius: 10px; }
        .m4r-field:focus-within { box-shadow: 0 0 0 3px rgba(var(--brand-overlay),0.25); border-color: var(--brand); }
      `}</style>

      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/30 to-transparent">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 pt-0 pb-1 flex items-center justify-between max-w-7xl gap-4">
          <Link to={HOME_PATH} aria-label={LOGO_ALT} className="inline-flex items-center rounded-lg">
            <img src={LOGO_URL} alt={LOGO_ALT} width={220} height={80}
              className="h-14 sm:h-16 max-w-[160px] sm:max-w-[200px] w-auto object-contain rounded-lg drop-shadow-xl"
              style={isGestos ? { filter: 'brightness(0) invert(1)' } : undefined} />
          </Link>
          <div className="flex items-center gap-2">
            <SeoLangSwitcher currentLang={lang} />
          </div>
        </div>
      </header>

      {/* Hero + Booking card */}
      <div className="relative overflow-hidden" style={{ minHeight: '640px' }}>
        <img src={heroImg} alt={heroAlt}
          className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '65% center' }}
          fetchpriority="high" loading="eager" decoding="sync" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(var(--brand-overlay),0.82) 0%, rgba(var(--brand-overlay),0.45) 50%, rgba(0,0,0,0.35) 100%)' }} />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 pt-20 pb-10 flex flex-col items-center text-center">
          <span className="hero-fade-1 inline-flex items-center gap-2 text-[var(--accent)] text-xs font-bold tracking-[0.2em] uppercase mb-4">
            <span className="w-6 h-px bg-[var(--accent)]" />{badge}<span className="w-6 h-px bg-[var(--accent)]" />
          </span>
          <h1 className="hero-fade-2 text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-5 text-white drop-shadow-xl max-w-3xl">
            {h1}
          </h1>
          <p className="hero-fade-3 text-white/90 text-sm sm:text-base mb-6 max-w-2xl">{heroP}</p>
          <div className="hero-fade-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-8">
            {[
              { Icon: Truck, label: hero?.trust1 ?? S.trust1 },
              { Icon: MessageCircle, label: hero?.trust2 ?? S.trust2 },
              { Icon: CheckCircle, label: hero?.trust3 ?? S.trust3 },
            ].map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-white/95 text-xs sm:text-sm font-medium">
                <Icon className="w-4 h-4 shrink-0 text-[var(--accent)]" />
                {label}
              </div>
            ))}
          </div>

          {/* Booking card */}
          <div className="w-full max-w-2xl bg-white/95 rounded-3xl shadow-2xl ring-1 ring-black/5 overflow-hidden" id="search-section">
            <div className="bg-gradient-to-r from-[var(--brand)] to-[var(--brand-dark)] px-5 sm:px-7 py-3 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                <Search className="w-4 h-4 text-white" />
              </div>
              <p className="text-white text-sm font-bold leading-tight">{S.findEquip}</p>
            </div>

            <div className="p-5 sm:p-7 space-y-4">
              {/* CITY */}
              <div>
                <label className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-[var(--brand)] tracking-wider mb-1.5">
                  <MapPin className="w-3 h-3" />{S.cityLabel}
                </label>
                <div className="m4r-field relative flex items-center bg-gray-50/50 px-3.5 transition-all hover:bg-white hover:shadow-sm">
                  <MapPin className="w-4 h-4 text-[var(--brand)] shrink-0" />
                  <CityAutocomplete
                    value={searchData.city}
                    onChange={(v) => { setSearchData({ ...searchData, city: v }); if (deliveryHint && v !== 'Lisboa') setDeliveryHint(''); }}
                    placeholder={S.whereAreYouGoing}
                    counties={counties}
                    inputClassName="w-full px-2.5 py-2.5 text-sm text-[var(--brand)] placeholder-gray-400 focus:outline-none bg-transparent font-medium" />
                  {searchData.city &&
                    <button type="button" onClick={() => setSearchData({ ...searchData, city: '' })} className="text-gray-400 hover:text-[var(--brand)] shrink-0 transition-colors">
                      <span className="text-lg leading-none">×</span>
                    </button>}
                </div>
              </div>

              {/* Return in another city toggle — só nas páginas Orthocare (não Gestos Ágeis) */}
              {!isGestos && (
              <label className="flex items-center gap-2.5 cursor-pointer select-none group">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={differentReturnCity}
                  aria-label={S.returnAnother}
                  onClick={() => {
                    setDifferentReturnCity((v) => {
                      const nv = !v;
                      if (!nv) setSearchData((s) => ({ ...s, returnCity: '' }));
                      return nv;
                    });
                  }}
                  className={`relative w-9 h-5 rounded-full transition-colors duration-200 shrink-0 ${differentReturnCity ? 'bg-[var(--brand)]' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${differentReturnCity ? 'translate-x-4' : ''}`} />
                </button>
                <span className="text-[11px] sm:text-xs font-semibold text-[var(--brand)] tracking-wide">{S.returnAnother}</span>
              </label>
              )}

              {differentReturnCity && !isGestos && (
                <div>
                  <label className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-[var(--brand)] tracking-wider mb-1.5">
                    <MapPin className="w-3 h-3" />{S.returnCityLabel}
                  </label>
                  <div className="m4r-field relative flex items-center bg-gray-50/50 px-3.5 transition-all hover:bg-white hover:shadow-sm">
                    <MapPin className="w-4 h-4 text-[var(--brand)] shrink-0" />
                    <CityAutocomplete
                      value={searchData.returnCity}
                      onChange={(v) => setSearchData({ ...searchData, returnCity: v })}
                      placeholder={S.whereReturn}
                      counties={counties}
                      inputClassName="w-full px-2.5 py-2.5 text-sm text-[var(--brand)] placeholder-gray-400 focus:outline-none bg-transparent font-medium" />
                    {searchData.returnCity &&
                      <button type="button" onClick={() => setSearchData({ ...searchData, returnCity: '' })} className="text-gray-400 hover:text-[var(--brand)] shrink-0 transition-colors">
                        <span className="text-lg leading-none">×</span>
                      </button>}
                  </div>
                </div>
              )}

              {/* DATES */}
              <div>
                <label className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-[var(--brand)] tracking-wider mb-1.5">
                  <Calendar className="w-3 h-3" />{S.datesLabel}
                </label>
                <div className="m4r-field bg-gray-50/50 [&_button]:w-full [&_button]:py-2.5 [&_button]:text-sm [&_button]:text-[var(--brand)] [&_button]:font-medium [&_button]:focus:outline-none [&_button]:px-3.5 [&_button]:flex [&_button]:items-center [&_button]:gap-2 transition-all hover:bg-white hover:shadow-sm">
                  <DateRangePicker
                    startDate={searchData.startDate}
                    endDate={searchData.endDate}
                    minDate={minStartDate}
                    lang={lang || 'pt'}
                    onChange={({ startDate, endDate }) => setSearchData((s) => ({ ...s, startDate, endDate }))} />
                </div>
              </div>

              {/* START / END */}
              <div className="grid grid-cols-2 gap-3">
                {[['pickupTime', S.startLabel], ['returnTime', S.endLabel]].map(([key, label]) => (
                  <div key={key}>
                    <label className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-[var(--brand)] tracking-wider mb-1.5">
                      <Clock className="w-3 h-3" />{label}
                    </label>
                    <TimeSelect
                      value={searchData[key]}
                      onChange={(v) => setSearchData((prev) => ({ ...prev, [key]: v }))}
                      minTime={key === 'pickupTime' ? minPickupStr : minReturnStr}
                      date={key === 'pickupTime' ? searchData.startDate : searchData.endDate}
                      language={lang}
                      iconColor="var(--brand)"
                      triggerClassName="m4r-field bg-gray-50/50 hover:bg-white hover:shadow-sm transition-all text-sm text-[var(--brand)]" />
                  </div>
                ))}
              </div>

              {/* SEARCH button */}
              <button
                type="button"
                onClick={() => {
                  const city = searchData.city.trim();
                  if (!city) { alert(S.pleaseEnterCity); return; }
                  // If counties haven't loaded yet (backend rate-limited and
                  // fallback still pending), skip strict validation and let
                  // the search proceed with the typed city — SearchResults
                  // handles unknown cities gracefully.
                  const cityMatch = counties.length > 0
                    ? counties.find((c) => normalizeText(c.name) === normalizeText(city))
                    : null;
                  if (counties.length > 0 && !cityMatch) { alert(S.invalidCity); return; }
                  let resolved = { ...searchData, city: cityMatch ? cityMatch.name : city };
                  if (differentReturnCity && !resolved.returnCity.trim()) { alert(S.pleaseEnterReturnCity); return; }
                  if (differentReturnCity) {
                    const returnMatch = counties.length > 0
                      ? counties.find((c) => normalizeText(c.name) === normalizeText(resolved.returnCity.trim()))
                      : null;
                    if (counties.length > 0 && !returnMatch) { alert(S.invalidCity); return; }
                    if (returnMatch) resolved.returnCity = returnMatch.name;
                  }
                  setSearchData(resolved);
                  handleSearch();
                }}
                className="group w-full mt-1 bg-gradient-to-r from-[var(--brand)] to-[var(--brand-dark)] hover:from-[var(--brand-dark)] hover:to-[var(--brand-darker)] text-white font-bold py-4 rounded-2xl text-sm sm:text-base transition-all cursor-pointer shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2.5 uppercase tracking-[0.15em] relative overflow-hidden">
                <span className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors" />
                <Search className="w-5 h-5 relative z-10" />
                <span className="relative z-10">{S.searchBtn}</span>
              </button>

              <div className="flex items-center justify-center gap-4 pt-1">
                {[
                  { Icon: CheckCircle, label: S.freeCancel },
                  { Icon: Lock, label: S.securePay },
                ].map(({ Icon, label }) => (
                  <div key={label} className="flex items-center gap-1 text-gray-400 text-[10px] font-medium">
                    <Icon className="w-3 h-3 text-[var(--brand)]" />
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom contact nav */}
      <nav className="z-40 bg-[var(--brand)] border-t border-white/10">
        <div className="max-w-3xl mx-auto grid grid-cols-3 divide-x divide-white/10">
          <a href="#" onClick={(e) => e.preventDefault()} className="flex items-center justify-center gap-2 py-3 text-white hover:bg-white/10 transition-colors">
            <MessageCircle className="w-4 h-4 text-[var(--accent)]" />
            <span className="text-xs font-bold">{S.liveChat}</span>
          </a>
          <a href="https://wa.me/351216035281" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 text-white hover:bg-white/10 transition-colors">
            <MessageCircle className="w-4 h-4 text-[var(--accent)]" />
            <span className="text-xs font-bold">WhatsApp</span>
          </a>
          <a href="tel:+351216035281" className="flex items-center justify-center gap-2 py-3 text-white hover:bg-white/10 transition-colors">
            <Phone className="w-4 h-4 text-[var(--accent)]" />
            <span className="text-xs font-bold">{S.call}</span>
          </a>
        </div>
      </nav>

      {/* Stats bar */}
      <div style={{ background: 'linear-gradient(135deg, var(--brand), var(--brand-darker))' }}>
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-white/20">
          {[
            { val: '4.9/5', label: S.googleRating, sub: '★', icon: Star },
            { val: '2.000+', label: S.customers, icon: CheckCircle },
            { val: '60+', label: S.cities, icon: MapPin },
            { val: '100%', label: S.securePaymentStat, icon: Lock },
          ].map(({ val, label, icon: Icon, sub }) => (
            <div key={label} className="py-5 text-center px-2">
              {Icon && <Icon className="w-4 h-4 text-white/40 mx-auto mb-1" />}
              <div className="text-xl sm:text-2xl font-black text-white flex items-center justify-center gap-1">
                {sub === '★' && <Star className="w-4 h-4 fill-amber-400 text-amber-400" />}
                {val}
              </div>
              <p className="text-[11px] text-white/70 mt-0.5 font-medium">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}